<template>
    <TableFieldSet title="Zahlung">
        <div class="col-12" style="max-width: 40rem">
            <div v-if="initData">
                <Message :severity="countdownSeverity" :closable="false" class="mb-3">
                    <div class="ml-2">
                        <span><strong>Wichtiger Hinweis:</strong> Ihre Plätze sind erst nach erfolgreicher Zahlung verbindlich reserviert.</span>
                        <span v-if="showCountdown">
                            Wir halten sie noch
                            <strong class="mx-1">{{ formattedRemaining }}</strong>
                            für Sie frei &ndash; danach werden sie wieder freigegeben
                            und Sie können die Buchung jederzeit neu starten.
                        </span>
                    </div>
                </Message>
                <div class="p-3 border-round surface-0 shadow-2 mb-3 bg-white">
                    <h3 class="text-xl font-bold mb-3 pb-2 border-bottom-1 surface-border">Zusammenfassung</h3>
                    <div class="grid mb-3">
                        <div class="col-12 md:col-6">
                            <div class="text-500 text-sm mb-1">Event</div>
                            <div class="font-bold">{{ initData.eventName || 'Unbekanntes Event' }}</div>
                        </div>
                        <div class="col-12 md:col-6">
                            <div class="text-500 text-sm mb-1">Datum</div>
                            <div class="font-bold">{{ initData.eventDate || '-' }}</div>
                        </div>
                    </div>
                    <div class="mt-4">
                        <div class="text-500 text-sm mb-2">Kostenaufstellung</div>
                        <div v-for="(item, idx) in (initData.lineItems || [])" :key="idx" class="flex justify-content-between align-items-center mb-2 p-2 surface-50 border-round">
                            <div>
                                <span class="font-bold mr-2">{{ item.quantity }}x</span>
                                <span>{{ cleanDescription(item.description) }}</span>
                                <div class="text-xs text-500 mt-1" v-if="item.quantity > 0">
                                    Einzelpreis: {{ formatCurrency(item.amountIncludingTax, initData.currency) }}
                                </div>
                            </div>
                            <div class="font-bold">
                                {{ formatCurrency(item.amountIncludingTax * item.quantity, initData.currency) }}
                            </div>
                        </div>
                        <div class="flex justify-content-between align-items-center mt-3 pt-3 border-top-1 surface-border">
                            <div class="text-lg font-bold">Gesamtsumme</div>
                            <div class="text-xl font-bold text-primary">
                                {{ formatCurrency(initData.amount, initData.currency) }}
                            </div>
                        </div>
                    </div>
                    <div class="text-xs text-500 mt-3 text-right">
                        Bestellnummer: {{ displayId || resid || '-' }}
                    </div>
                </div>
            </div>
            <div class="p-3 border-round surface-100 surface-border">
                <Message v-if="expired" severity="warn" :closable="false" class="mb-3">
                    <div class="ml-2">
                        <div class="mb-2">
                            Der Reservierungszeitraum ist abgelaufen und Ihre Plätze wurden wieder freigegeben.
                            Es wurde nichts abgebucht.
                        </div>
                        <Button label="Buchung neu starten" size="small" @click="restartBooking" />
                    </div>
                </Message>
                <Message v-else-if="errorMsg" severity="error" :closable="false" class="mb-3">{{ errorMsg }}</Message>
                <div v-show="!expired" id="dropin-container" style="min-height: 340px; display: flex; align-items: center; justify-content: center;">
                </div>
                <div v-if="loading" class="fa-3x"><i class="fas fa-spinner fa-spin"></i></div>
            </div>
        </div>
    </TableFieldSet>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AdyenCheckout, Dropin, Card, GooglePay, ApplePay, SepaDirectDebit, Klarna } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import { useCrud } from '@/composables/CrudAxios';
import TableFieldSet from '@/components/TableFieldSet.vue';
import Message from 'primevue/message';
import Button from 'primevue/button';

const route = useRoute();
const router = useRouter();
const { getAsync, postFormAsync } = useCrud();

const resid = computed(() => String(route.query.resid || ''));
const loading = ref(true);
const cfg = ref<any>(null);
const initData = ref<any>(null);
const errorMsg = ref<string>('');
const displayId = ref<string>('');
const dropinInstance = ref<Dropin | null>(null);

// Remaining time of the payment window (seats are held for a limited time only)
const secondsRemaining = ref<number | null>(null);
const expired = ref(false);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const showCountdown = computed(() => !expired.value && secondsRemaining.value !== null && secondsRemaining.value > 0);
// Stay calm as long as there is plenty of time left, only hint gently at the end
const countdownSeverity = computed(() => ((secondsRemaining.value ?? 0) <= 120 ? 'warn' : 'info'));
const formattedRemaining = computed(() => {
    const total = Math.max(0, secondsRemaining.value ?? 0);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')} Minuten`;
});

function stopCountdown() {
    if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
    }
}

function onPaymentWindowExpired() {
    stopCountdown();
    secondsRemaining.value = 0;
    expired.value = true;
    errorMsg.value = '';
    unmountCheckout();
}

function startCountdown(seconds: number) {
    stopCountdown();
    secondsRemaining.value = Math.max(0, Math.floor(seconds));
    if (secondsRemaining.value <= 0) {
        onPaymentWindowExpired();
        return;
    }
    countdownTimer = setInterval(() => {
        secondsRemaining.value = Math.max(0, (secondsRemaining.value ?? 0) - 1);
        if (secondsRemaining.value <= 0) {
            onPaymentWindowExpired();
        }
    }, 1000);
}

async function restartBooking() {
    await router.replace({ name: 'bookingDetails', query: { store: route.query.store } });
}

function formatCurrency(cents: number, currency: string = 'EUR') {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency });
}

function cleanDescription(desc: string) {
    // Backend returns e.g. "3 x Ticket(s)". We want to remove the "3 x " prefix if we display quantity separately.
    return desc ? desc.replace(/^\d+\s+x\s+/, '') : '';
}

async function initCheckout() {
    try {
        // 1) fetch static payment config
        cfg.value = await getAsync(`/extern/table/paymentconfig?store=${route.query.store}`);
        if (!cfg.value || !cfg.value.clientKey) {
            errorMsg.value = 'Zahlung ist nicht konfiguriert. Bitte wenden Sie sich an das Restaurant.';
            return;
        }

        // 2) init session for this reservation
        initData.value = await postFormAsync(`/extern/table/paymentinit?store=${route.query.store}`, { resid: resid.value });
        if (!initData.value?.success) {
            errorMsg.value = initData.value?.message || 'Zahlung konnte nicht gestartet werden.';
            return;
        }
        if (typeof initData.value.secondsRemaining === 'number') {
            startCountdown(initData.value.secondsRemaining);
        }

        const checkout = await AdyenCheckout({
            environment: initData.value.env || cfg.value.env,
            clientKey: cfg.value.clientKey,
            analytics: { enabled: false },
            session: {
                id: initData.value.sessionId,
                sessionData: initData.value.sessionData
            },
            onPaymentCompleted: (result: any) => {
                if (result?.resultCode === 'Authorised') {
                    stopCountdown();
                    router.replace({ name: 'success', query: { store: route.query.store, resid: resid.value, flow: 'event' } });
                } else if (result?.resultCode === 'Cancelled') {
                    router.replace({ name: 'bookingDetails', query: { store: route.query.store } });
                } else {
                    errorMsg.value = 'Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.';
                }
            },
            onError: () => {
                errorMsg.value = 'Zahlung konnte nicht verarbeitet werden.';
            }
        });
        const container = document.getElementById('dropin-container');
        if (container) {
            dropinInstance.value = new Dropin(checkout, {
                paymentMethodsConfiguration: {
                    card: { hasHolderName: true, holderNameRequired: true, billingAddressRequired: true }
                },
                paymentMethodComponents: [Card, GooglePay, ApplePay, SepaDirectDebit, Klarna]
            });
            dropinInstance.value.mount('#dropin-container');
        }
    } catch (e) {
        console.log(e);
        // keep user on page and show error
        errorMsg.value = 'Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu oder versuchen Sie es später erneut.';
    } finally {
        loading.value = false;
    }
}
function unmountCheckout() {
    if (dropinInstance.value) {
        dropinInstance.value.unmount();
        dropinInstance.value = null;
    }
}

onUnmounted(() => {
    stopCountdown();
    unmountCheckout();
});
onMounted(async () => {
    if (!resid.value) {
        await router.replace({ name: 'bookingDetails', query: { store: route.query.store } });
        return;
    }

    // Check status first to prevent double payment or invalid state
    try {
        const statusRes: any = await getAsync(`/extern/table/checkreservation?store=${route.query.store}&resid=${encodeURIComponent(resid.value)}`);
        if (statusRes && statusRes.success && statusRes.data) {
            if (statusRes.data.resid) {
                displayId.value = String(statusRes.data.resid);
            }
            if (statusRes.data.status === 2) {
                // Already paid
                await router.replace({ name: 'success', query: { store: route.query.store, resid: resid.value, flow: 'event' } });
                return;
            }
            if (statusRes.data.status === 3) {
                // Payment window already elapsed, the seats have been released again
                onPaymentWindowExpired();
                loading.value = false;
                return;
            }
            if (typeof statusRes.data.secondsRemaining === 'number') {
                startCountdown(statusRes.data.secondsRemaining);
            }
        }
    } catch (e) {
        // ignore, initCheckout will likely fail if invalid
    }

    if (route.query.failed === '1') {
        errorMsg.value = 'Die Zahlung ist fehlgeschlagen oder wurde abgebrochen. Bitte versuchen Sie es erneut.';
    }
    await initCheckout();
});
</script>